/**
 * Create / edit one of the standard letters, with a live preview that uses
 * sample values so the author sees "Dear James" rather than "Dear {{firstName}}".
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EVENT_AUDIENCE_LABELS,
  EVENT_AUDIENCE_PRESETS,
  MESSAGE_CATEGORIES,
  MESSAGE_CATEGORY_LABELS,
  SAMPLE_MERGE_VARS,
  applyMergeFields,
  type CommsTemplate,
  type EventAudiencePreset,
  type MessageCategory,
  type SendableChannel,
  type TemplateInput,
  type TemplateScope,
} from '@gwc/shared';
import { commsApi } from '../api/comms';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { Button, Dialog, Input, Select } from './ui';
import { MergeFieldChips, insertAtCaret } from './MergeFieldHelp';

export function TemplateEditor({
  open,
  onClose,
  template,
}: {
  open: boolean;
  onClose: () => void;
  /** Undefined = create a new one. */
  template?: CommsTemplate;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<TemplateInput>(() => toForm(template));
  const [error, setError] = useState<string | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [lastFocus, setLastFocus] = useState<'subject' | 'body'>('body');

  useEffect(() => {
    if (open) {
      setForm(toForm(template));
      setError(null);
    }
  }, [open, template]);

  const set = (patch: Partial<TemplateInput>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () =>
      template
        ? commsApi.updateTemplate(template.id, form)
        : commsApi.createTemplate(form),
    onSuccess: () => {
      toast.success(template ? 'Template saved.' : 'Template created.');
      void qc.invalidateQueries({ queryKey: ['comms', 'templates'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save the template'),
  });

  function insert(token: string) {
    if (lastFocus === 'subject') {
      set({ subject: insertAtCaret(subjectRef.current, token, form.subject) });
    } else {
      set({ body: insertAtCaret(bodyRef.current, token, form.body) });
    }
  }

  const toggleChannel = (c: SendableChannel) =>
    set({
      channels: form.channels.includes(c)
        ? form.channels.filter((x) => x !== c)
        : [...form.channels, c],
    });

  const valid = form.name.trim() && form.subject.trim() && form.body.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={template ? `Edit “${template.name}”` : 'New template'}
      description={
        template?.isSystem
          ? 'A built-in letter. The automatic emails use this copy — edits take effect on the next send.'
          : 'A reusable letter for the composer and the tasting page.'
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-cream-300/50">{error ? <span className="text-danger">{error}</span> : null}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>
              {template ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[1fr_260px]">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              value={form.name}
              maxLength={120}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Final details for attendees"
            />
            <Select
              label="Type"
              value={form.scope}
              disabled={template?.isSystem}
              onChange={(e) => set({ scope: e.target.value as TemplateScope })}
              hint={form.scope === 'event' ? 'Sent about one tasting; event fields available.' : 'A general letter to members.'}
            >
              <option value="general">General</option>
              <option value="event">About a tasting</option>
            </Select>
          </div>
          <Input
            label="Description"
            value={form.description ?? ''}
            maxLength={500}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="When to use this one"
          />
          <div>
            <label className="mb-1 block text-sm text-cream-200" htmlFor="tpl-subject">
              Subject
            </label>
            <input
              id="tpl-subject"
              ref={subjectRef}
              className="field"
              value={form.subject}
              maxLength={140}
              onFocus={() => setLastFocus('subject')}
              onChange={(e) => set({ subject: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-cream-200" htmlFor="tpl-body">
              Body
            </label>
            <textarea
              id="tpl-body"
              ref={bodyRef}
              className="field min-h-[220px] font-mono text-[13px]"
              value={form.body}
              maxLength={5000}
              onFocus={() => setLastFocus('body')}
              onChange={(e) => set({ body: e.target.value })}
            />
            <p className="mt-1 text-xs text-cream-300/45">
              Plain text. Blank lines start a new paragraph; links become clickable in the email.
            </p>
          </div>
          <MergeFieldChips
            onInsert={insert}
            scopes={form.scope === 'event' ? ['person', 'club', 'event'] : ['person', 'club']}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Category"
              value={form.category}
              onChange={(e) => set({ category: e.target.value as MessageCategory })}
              hint="Members mute by category."
            >
              {MESSAGE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {MESSAGE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
            {form.scope === 'event' ? (
              <Select
                label="Default audience"
                value={form.eventAudience ?? 'registered'}
                onChange={(e) => set({ eventAudience: e.target.value as EventAudiencePreset })}
              >
                {EVENT_AUDIENCE_PRESETS.map((p) => (
                  <option key={p} value={p}>
                    {EVENT_AUDIENCE_LABELS[p]}
                  </option>
                ))}
              </Select>
            ) : (
              <div>
                <span className="mb-1 block text-sm text-cream-200">Default channels</span>
                <div className="flex gap-4 pt-2">
                  {(['push', 'email'] as const).map((c) => (
                    <label key={c} className="flex cursor-pointer items-center gap-2 text-sm text-cream-200">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-gold-500"
                        checked={form.channels.includes(c)}
                        onChange={() => toggleChannel(c)}
                      />
                      {c === 'push' ? 'Push' : 'Email'}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <TemplatePreview subject={form.subject} body={form.body} />
      </div>
    </Dialog>
  );
}

export function TemplatePreview({
  subject,
  body,
  vars,
  label = 'Preview with sample values',
}: {
  subject: string;
  body: string;
  /** Resolved values; defaults to the sample set. */
  vars?: Record<string, string | null | undefined>;
  label?: string;
}) {
  const v = vars ?? SAMPLE_MERGE_VARS;
  return (
    <aside className="rounded-lg border border-ink-800 bg-cream-50/[0.03] p-4">
      <p className="mb-2 text-[11px] uppercase tracking-wide text-cream-300/50">{label}</p>
      <p className="font-display text-base text-cream-50">
        {applyMergeFields(subject, v, { strip: true }) || <span className="text-cream-300/40">Subject…</span>}
      </p>
      <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-cream-200">
        {applyMergeFields(body, v, { strip: true }) || <span className="text-cream-300/40">Body…</span>}
      </div>
    </aside>
  );
}

function toForm(t?: CommsTemplate): TemplateInput {
  return t
    ? {
        name: t.name,
        description: t.description ?? '',
        scope: t.scope as TemplateScope,
        category: t.category as MessageCategory,
        subject: t.subject,
        body: t.body,
        channels: t.channels,
        eventAudience: t.eventAudience ?? undefined,
      }
    : {
        name: '',
        description: '',
        scope: 'general',
        category: 'announcements',
        subject: '',
        body: 'Dear {{firstName}},\n\n',
        channels: ['push', 'email'],
        eventAudience: undefined,
      };
}
